<template>
  <div class="k-wrapper">
    <side-bar title="Kemaris" :sidebar-links="sidebarLinks">
      <template slot="links">
        <!-- Verschachteltes Menü -->
        <collapsible-sidebar-link
          v-for="link in sidebarLinksWithChildren"
          :key="`collapsible-${link.path || link.name}`"
          :name="link.name"
          :icon="link.icon"
          :use-shield-icon="link.useShieldIcon || false"
          :use-euro-icon="link.useEuroIcon || false"
          :children="link.children"
          :path="link.path"
        />
        <!-- Normaler Link -->
        <sidebar-link
          v-for="link in sidebarLinksWithoutChildren"
          :key="link.path"
          :to="link.path"
          :name="link.name"
          :icon="link.icon"
          :use-shield-icon="link.useShieldIcon || false"
          :use-euro-icon="link.useEuroIcon || false"
        />
      </template>
    </side-bar>

    <div class="k-main">
      <top-navbar />
      <div class="k-content">
        <router-view />
      </div>
      <content-footer />
    </div>

    <!-- Mobile Overlay -->
    <div
      v-if="$sidebar.showSidebar"
      class="k-overlay"
      @click="toggleSidebar"
    ></div>

    <!-- Kimi Chat Bot -->
    <kimi-chat />
  </div>
</template>

<script>
import TopNavbar from "./dashboard/TopNavbar.vue";
import ContentFooter from "./dashboard/ContentFooter.vue";
import CollapsibleSidebarLink from "@/components/SidebarPlugin/CollapsibleSidebarLink.vue";
import KimiChat from "@/components/chat/KimiChat.vue";
import { UserRoles } from "@/stores/authStore";

/**
 * DashboardLayout - Main dashboard layout with role-based navigation
 * @description Wrapper for all authenticated pages with dynamic sidebar
 */
export default {
  name: "DashboardLayout",
  components: {
    TopNavbar,
    ContentFooter,
    CollapsibleSidebarLink,
    KimiChat,
  },
  computed: {
    /**
     * Returns navigation links based on user role
     * @returns {Array}
     */
    sidebarLinks() {
      const role = this.$auth?.role;

      if (role === UserRoles.INVESTOR) {
        return [
          {
            path: "/investor/dashboard",
            name: "Portfolio",
            icon: "tim-icons icon-chart-pie-36",
          },
          {
            path: "/investor/compare",
            name: "Vergleich",
            icon: "tim-icons icon-chart-bar-32",
          },
          {
            path: "/investor/affiliate-dashboard",
            name: "Affiliate Dashboard",
            icon: "tim-icons icon-money-coins",
          },
          {
            name: "Administration",
            icon: "tim-icons icon-settings",
            children: [
              {
                path: "/profile",
                name: "Profil",
                icon: "tim-icons icon-single-02",
              },
            ],
          },
          {
            path: "/investor/chat",
            name: "Chat",
            icon: "tim-icons icon-chat-33",
          },
        ];
      }

      if (role === UserRoles.ENTREPRENEUR) {
        return [
          {
            name: "Dashboard",
            icon: "tim-icons icon-chart-pie-36",
            children: [
              {
                path: "/entrepreneur/controlcenter",
                name: "Dashboard",
                icon: "tim-icons icon-laptop",
              },
              {
                path: "/entrepreneur/financial-health",
                name: "Financial Health",
                icon: "tim-icons icon-money-coins",
              },
              {
                path: "/entrepreneur/management-dna",
                name: "Management DNA",
                icon: "tim-icons icon-molecule-40",
              },
              {
                path: "/entrepreneur/market-traction",
                name: "Market Traction",
                icon: "tim-icons icon-chart-bar-32",
              },
            ],
          },
          {
            name: "Investoren",
            icon: "tim-icons icon-bank",
            children: [
              {
                path: "/entrepreneur/investors",
                name: "Investoren",
                icon: "tim-icons icon-bank",
              },
              {
                path: "/entrepreneur/cap-table",
                name: "Cap Table",
                icon: "tim-icons icon-chart-bar-32",
              },
            ],
          },
          {
            name: "Datenbasis",
            icon: "tim-icons icon-notes",
            children: [
              {
                path: "/entrepreneur/metrics",
                name: "Umsatzplanung",
                icon: "tim-icons icon-notes",
              },
              {
                path: "/entrepreneur/cost-planning",
                name: "Kostenplanung",
                icon: "tim-icons icon-coins",
              },
              {
                path: "/entrepreneur/personalplanung",
                name: "Personalplanung",
                icon: "tim-icons icon-single-02",
              },
              {
                path: "/entrepreneur/contract-management",
                name: "Vertragsmanagement",
                icon: "tim-icons icon-paper",
              },
              {
                path: "/entrepreneur/cashmanagement",
                name: "Cashmanagement",
                icon: "tim-icons icon-wallet-43",
              },
              {
                path: "/entrepreneur/kundenmanagement",
                name: "Kundenmanagement",
                icon: "tim-icons icon-badge",
              },
            ],
          },
          {
            path: "/entrepreneur/affiliate-dashboard",
            name: "Affiliate Dashboard",
            icon: "tim-icons icon-money-coins",
          },
          {
            path: "/entrepreneur/partner-dashboard",
            name: "Partner Dashboard",
            icon: "tim-icons icon-single-02",
          },
          {
            name: "Grant Pilot",
            icon: "tim-icons icon-spaceship",
            children: [
              {
                path: "/entrepreneur/cashradar",
                name: "Cashradar",
                icon: "",
                useEuroIcon: true,
              },
              {
                path: "/entrepreneur/risk-shield",
                name: "Risk Shield",
                icon: "",
                useShieldIcon: true,
              },
              {
                path: "/entrepreneur/buchhaltung-steuer",
                name: "TaxTool",
                icon: "tim-icons icon-paper",
              },
            ],
          },
          {
            name: "Administration",
            icon: "tim-icons icon-settings",
            children: [
              {
                path: "/entrepreneur/users",
                name: "Benutzer",
                icon: "tim-icons icon-badge",
              },
              {
                path: "/profile",
                name: "Profil",
                icon: "tim-icons icon-single-02",
              },
            ],
          },
          {
            path: "/entrepreneur/chat",
            name: "Chat",
            icon: "tim-icons icon-chat-33",
          },
        ];
      }

      // Default fallback
      return [
        {
          name: "Administration",
          icon: "tim-icons icon-settings-gear-65",
          children: [
            {
              path: "/profile",
              name: "Profil",
              icon: "tim-icons icon-single-02",
            },
          ],
        },
      ];
    },
    /**
     * Returns links with children (collapsible)
     * @returns {Array}
     */
    sidebarLinksWithChildren() {
      return this.sidebarLinks.filter(link => link.children && link.children.length > 0);
    },
    /**
     * Returns links without children (normal links)
     * @returns {Array}
     */
    sidebarLinksWithoutChildren() {
      return this.sidebarLinks.filter(link => !link.children || link.children.length === 0);
    },
  },
  methods: {
    /**
     * Toggles sidebar visibility on mobile
     */
    toggleSidebar() {
      if (this.$sidebar.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
    },
  },
};
</script>

<style scoped>
.k-wrapper {
  display: flex;
  min-height: 100vh;
  background: var(--k-bg-secondary);
  transition: background-color var(--k-transition-normal);
}

.k-main {
  flex: 1;
  margin-left: var(--k-sidebar-width, 260px);
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left var(--k-transition-normal);
}

.k-content {
  flex: 1;
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

.k-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  opacity: 1;
  transition: opacity var(--k-transition-normal);
}

@media (max-width: 991px) {
  .k-main {
    margin-left: 0;
  }
}

@media (max-width: 768px) {
  .k-content {
    padding: 20px;
  }
}
</style>
